import { ChevronDown } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useState } from 'react';

import type { CalendarDayRow, CalendarEarning } from '~/types/earningsCalendar.types';

import {
  DAY_PREVIEW_LIMIT_DESKTOP,
  DAY_PREVIEW_LIMIT_MOBILE,
  PAGE_REVEAL_EASE,
  dayEntranceDelay,
} from '../constants/pageReveal.constants';
import CompanyTile from './CompanyTile';
import MoreCompaniesMenu from './MoreCompaniesMenu';

function TodayBadge() {
  return (
    <span className="inline-flex w-fit shrink-0 items-center rounded-full bg-emerald-700 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white">
      Today
    </span>
  );
}

function EmptyDay() {
  return (
    <div className="flex items-center justify-center rounded-xl border border-dashed border-slate-900/[0.08] bg-slate-50/40 px-4 py-8">
      <p className="text-sm text-slate-400">No earnings scheduled</p>
    </div>
  );
}

function DayCompanyGrid({
  items,
  previewLimit,
  className,
}: {
  items: CalendarEarning[];
  previewLimit: number;
  className: string;
}) {
  const hasOverflow = items.length > previewLimit;
  const visibleItems = hasOverflow ? items.slice(0, previewLimit) : items;
  const overflowItems = hasOverflow ? items.slice(previewLimit) : [];

  return (
    <div className={className}>
      {visibleItems.map((item, i) => (
        <CompanyTile key={`${item.symbol}-${item.name}-${i}`} item={item} />
      ))}
      {hasOverflow ? <MoreCompaniesMenu items={overflowItems} /> : null}
    </div>
  );
}

function companyCountLabel(count: number) {
  if (count === 0) return 'None';
  return `${count} ${count === 1 ? 'company' : 'companies'}`;
}

export default function DaySection({
  day,
  items,
  sequence,
  isToday,
}: {
  day: CalendarDayRow;
  items: CalendarEarning[];
  sequence: number;
  isToday: boolean;
  isCurrentWeek?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const [expanded, setExpanded] = useState(false);

  const hidden = reduceMotion ? false : { opacity: 0, y: 16 };
  const visible = { opacity: 1, y: 0 };
  const transition = {
    duration: reduceMotion ? 0.01 : 0.42,
    delay: reduceMotion ? 0 : dayEntranceDelay(sequence),
    ease: PAGE_REVEAL_EASE,
  };

  const countLabel = companyCountLabel(items.length);

  return (
    <motion.section
      aria-label={`${day.weekdayLong}, ${day.date}`}
      aria-current={isToday ? 'date' : undefined}
      className={`overflow-hidden rounded-xl border bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03),0_12px_32px_-20px_rgba(15,23,42,0.12)] ${
        isToday
          ? 'border-slate-900/[0.06] border-l-[3px] border-l-emerald-600'
          : 'border-slate-900/[0.06]'
      }`}
      initial={hidden}
      animate={visible}
      transition={transition}
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        className={`flex w-full flex-wrap items-center justify-between gap-2 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-emerald-600 md:px-5 md:py-3.5 ${
          expanded ? 'border-b border-slate-900/[0.04]' : ''
        }`}
      >
        <div className="flex min-w-0 items-center gap-2">
          <ChevronDown
            className={`size-4 shrink-0 transition-transform duration-200 ${
              expanded ? 'rotate-0' : '-rotate-90'
            } ${isToday ? 'text-emerald-600' : 'text-slate-400'}`}
            strokeWidth={2.5}
            aria-hidden
          />
          <div className="flex min-w-0 items-baseline gap-2.5">
            <h2 className="text-base font-extrabold leading-tight tracking-[-0.02em] text-slate-900 md:text-lg">
              <span className="sm:hidden">{day.weekdayShort}</span>
              <span className="hidden sm:inline">{day.weekdayLong}</span>
            </h2>
            <span
              className={`text-xs font-semibold tabular-nums md:text-sm ${
                isToday ? 'text-emerald-700' : 'text-slate-500'
              }`}
            >
              {day.date}
            </span>
            {isToday ? <TodayBadge /> : null}
          </div>
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
          {countLabel}
        </span>
      </button>

      {expanded ? (
        <div className="p-3 md:p-4">
          {items.length === 0 ? (
            <EmptyDay />
          ) : (
            <>
              <DayCompanyGrid
                items={items}
                previewLimit={DAY_PREVIEW_LIMIT_MOBILE}
                className="grid grid-cols-3 gap-2 md:hidden"
              />
              <DayCompanyGrid
                items={items}
                previewLimit={DAY_PREVIEW_LIMIT_DESKTOP}
                className="hidden grid-cols-4 gap-2.5 md:grid"
              />

            </>
          )}
        </div>
      ) : null}
    </motion.section>
  );
}
